import type { ManualEntryContext, ManualEntryParseResult, ManualEntrySource } from "../types/manualEntry";

export const BUS_ENTRY_SERVICES = ["当地用车", "单送", "单接", "接送"] as const;
export type BusEntryTableField = "range" | "service" | "vehicle" | "count" | "price";
export type BusEntryTableRow = {
  record: ManualEntrySource; partIndex: number; range: string; service: string;
  vehicle: string; count: string; price: string; editable: boolean; issues: string[];
};
const valueOf = (value = "") => /^(范围|日期|天次|送日,接日|车型|辆数|单价)$/.test(value) ? "" : value;

/** 提取一条用车记录的范围、服务与车型片段。 */
function recordParts(text: string) {
  const normal = text.normalize("NFKC").trim();
  const header = normal.match(/^(.+?)\s+(包车|当地用车|单接|单送|接送)\s+([\s\S]*)$/u);
  const bare = !header ? normal.match(/^(\d+\s*[-~至]\s*\d+)\s+([\s\S]+)$/u) : null;
  if (!header && !bare) return null;
  return {
    range: (header?.[1] ?? bare![1]).trim(),
    service: header?.[2] === "包车" ? "当地用车" : header?.[2] ?? "当地用车",
    vehicles: (header?.[3] ?? bare![2]).split(/[,，、]/u).map(part => part.trim()),
  };
}

/** 一行表格对应一个车型；成对接送仍由原解析器展开，不能重复生成两次。 */
export function busEntryTableRows(text: string, preview: ManualEntryParseResult): BusEntryTableRow[] {
  return [...text.matchAll(/[^\n;；]+/gu)].filter(match => match[0].trim()).flatMap(match => {
    const start = (match.index ?? 0) + match[0].search(/\S/u), raw = match[0].trim();
    const record = { text: raw, start, end: start + raw.length, line: text.slice(0, start).split("\n").length };
    const parts = recordParts(raw);
    const items = preview.items.filter(item => item.source.start >= record.start && item.source.end <= record.end);
    return (parts?.vehicles ?? [raw]).map((vehicleText, partIndex) => {
      const formula = vehicleText.match(/^(\d+座(?:大巴)?)([+\-\d][\s\S]*[xX×*][\s\S]*)$/u) ?? vehicleText.match(/^(.+?)\s+((?:[^\s×*]+(?:\s+辆)?)\s*[xX×*]\s*[\s\S]*)$/u) ?? vehicleText.match(/^(.+?)\s+([\s\S]+)$/u);
      const factors = (formula?.[2] ?? "").replace(/\s/gu, "").split(/[xX×*]/u).map(value => value.replace(/(?<=\d)(?:辆|元)$/u, ""));
      const issues = items.filter(item => item.source.start === record.start || item.source.text.normalize("NFKC").trim() === vehicleText)
        .flatMap(item => item.issues.map(issue => issue.message));
      return {
        record, partIndex, range: valueOf(parts?.range), service: parts?.service ?? "",
        vehicle: valueOf(formula?.[1]), count: valueOf(factors[0]), price: valueOf(factors[1]),
        editable: Boolean(parts && (!vehicleText || (formula && factors.length === 2))),
        issues: [...new Set(issues)],
      };
    });
  });
}

/** 替换指定原文记录，删空时清理相邻分隔符。 */
function replaceRecord(text: string, row: BusEntryTableRow, records: string[]): string {
  if (records.length) return text.slice(0, row.record.start) + records.join("\n") + text.slice(row.record.end);
  let { start, end } = row.record;
  const after = text.slice(end).match(/^[\s;；]+/u)?.[0];
  if (after) end += after.length;
  else start -= text.slice(0, start).match(/[\s;；]+$/u)?.[0].length ?? 0;
  const next = text.slice(0, start) + text.slice(end);
  return /^[\s;；]*$/u.test(next) ? "" : next;
}

/** 修改当前车型时展开同行其他车型，各自保留原范围和服务，其他原文不改写。 */
export function changeBusEntryRow(text: string, row: BusEntryTableRow, field: BusEntryTableField, value: string): string {
  const parts = recordParts(row.record.text);
  if (!row.editable || !parts || /[\n\r;；]/u.test(value)) return text;
  if (field !== "range" && /[,，、×*]/u.test(value)) return text;
  if ((field === "count" || field === "price") && /[xX]/u.test(value)) return text;
  if (field === "service" && !(BUS_ENTRY_SERVICES as readonly string[]).includes(value)) return text;
  const next = { ...row, [field]: value };
  const record = `${next.range || "范围"} ${next.service || "当地用车"} ${next.vehicle || "车型"} ${next.count || "辆数"}×${next.price || "单价"}`;
  return replaceRecord(text, row, parts.vehicles.map((vehicle, index) => index === row.partIndex ? record : `${parts.range} ${parts.service} ${vehicle}`));
}

/** 删除当前车型，保留同行其他车型的范围和服务。 */
export function removeBusEntryRow(text: string, row: BusEntryTableRow): string {
  const parts = recordParts(row.record.text);
  return replaceRecord(text, row, parts ? parts.vehicles.filter((_, index) => index !== row.partIndex).map(vehicle => `${parts.range} ${parts.service} ${vehicle}`) : []);
}

/** 沿用最近用车范围与车型新增待补价行。 */
export function appendBusEntryRow(text: string, context: ManualEntryContext, rows: BusEntryTableRow[]): string {
  const last = rows.filter(row => row.editable).at(-1);
  const range = last?.range || String(context.itineraryDays?.[0]?.day ?? "范围");
  const vehicle = last?.vehicle || context.vehicleTypes?.[0]?.name || "车型";
  return `${text}${text && !text.endsWith("\n") ? "\n" : ""}${range} ${last?.service || "当地用车"} ${vehicle} 1×单价`;
}
